/**
 * Installing the app on the home screen.
 *
 * Not cosmetics: on iOS, Web Push only exists for an installed web app, so this
 * is the step that decides whether notifications can work at all there. The two
 * platforms could not be less alike about it — Chromium fires an event the page
 * may replay later, Safari offers nothing and expects the user to know about the
 * share sheet — so this module reports which of the two situations applies and
 * leaves the wording to the view.
 */

/** Chromium's install event, which is not in the DOM typings. */
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

/**
 * Starts listening at module load.
 *
 * `beforeinstallprompt` fires once, early, and is gone if nobody caught it —
 * long before any settings sheet is open.
 */
export function watchInstallPrompt(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Keeping the event is what allows the prompt to be shown from a button
    // later instead of whenever the browser felt like it.
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    for (const listener of listeners) {
      listener();
    }
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    for (const listener of listeners) {
      listener();
    }
  });
}

export function onInstallStateChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** True once the app runs from the home screen rather than a browser tab. */
export function isInstalled(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    // Safari's own flag, which predates display-mode and is still what iOS sets.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function canPrompt(): boolean {
  return deferred !== null;
}

/** iOS has no install API at all; the share sheet is the only way in. */
export function needsManualInstall(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) && !isInstalled();
}

export async function promptInstall(): Promise<boolean> {
  const event = deferred;
  if (!event) {
    return false;
  }
  await event.prompt();
  const { outcome } = await event.userChoice;
  // The event is single-use: a dismissed prompt cannot be shown again from here.
  deferred = null;
  for (const listener of listeners) {
    listener();
  }
  return outcome === "accepted";
}
